import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Gem, Heart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { formatINR } from "@/lib/api";
import { toast } from "sonner";

export const ProductCard = ({ product, wide = false, testid }) => {
  const { addItem, openCart } = useCart();
  const { has: inWishlist, toggle: toggleWishlist } = useWishlist();
  const img = product.images?.[0];
  const saved = inWishlist(product.id);

  const handleAdd = (e) => {
    e.preventDefault();
    addItem(product, 1);
    toast.success(`${product.name} added to your cart`, {
      action: { label: "View", onClick: openCart },
    });
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    toggleWishlist(product);
    toast.success(saved ? "Removed from your wishlist" : "Saved to your wishlist");
  };

  return (
    <motion.article
      data-testid={testid}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="group flex h-full flex-col"
    >
      <Link to={`/product/${product.id}`} className="block flex-1 pb-3" data-testid={`${testid}-link`}>
        <div
          className={`relative overflow-hidden rounded-sm bg-cream-dark aspect-square`}
        >
          {img ? (
            <img
              src={img}
              alt={product.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Gem size={32} strokeWidth={1} className="text-gold-muted/40" />
            </div>
          )}
          <div className="absolute left-3 top-3 flex flex-col gap-2">
            {product.new_arrival && (
              <span className="bg-espresso px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
                New
              </span>
            )}
            {product.featured && (
              <span className="bg-gold px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-espresso">
                Featured
              </span>
            )}
          </div>
          <button
            data-testid={`${testid}-wishlist-toggle`}
            onClick={handleWishlist}
            className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-cream/85 backdrop-blur transition-all duration-300 hover:scale-110 active:scale-95"
            aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
          >
            <Heart
              size={15}
              className={saved ? "fill-gold-accent text-gold-accent" : "text-espresso/60"}
            />
          </button>
          {!product.in_stock && (
            <div className="absolute inset-0 flex items-center justify-center bg-cream/70 backdrop-blur-[2px]">
              <span className="border border-espresso/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso">
                Out of Stock
              </span>
            </div>
          )}
        </div>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-muted">
              {product.category}
            </p>
            <h3 className="mt-1 min-h-[3.4rem] font-serif text-xl leading-snug text-espresso line-clamp-2 transition-colors duration-300 group-hover:text-gold-accent">
              {product.name}
            </h3>
          </div>
          <div className="min-h-[3.4rem] text-right">
            <p data-testid={`${testid}-price`} className="price-text text-xl text-espresso">{formatINR(product.price)}</p>
            {product.original_price > product.price && (
              <p className="price-text text-xs font-normal text-espresso/50 line-through">
                {formatINR(product.original_price)}
              </p>
            )}
          </div>
        </div>
      </Link>
      <button
        data-testid={`${testid}-add-to-cart`}
        onClick={handleAdd}
        disabled={!product.in_stock}
        className="mt-auto flex items-center justify-center gap-2 border border-[#C5A15B] bg-[#C5A15B] py-3 font-sans text-xs font-bold uppercase tracking-[0.2em] text-espresso transition-all duration-300 hover:bg-gold-accent hover:border-gold-accent active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[#C5A15B] disabled:hover:bg-[#C5A15B]"
      >
        <Plus size={13} /> Add to Cart
      </button>
    </motion.article>
  );
};
